import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';


const topLevelAwaitCode = `// Structural Logic: Clean Initialization (ES2022)
// Before: Needed IIFE or async wrapper to use await
// After: Module evaluation itself is the "async function"

// database.js
const dbConnection = await createPool({ host: 'db.prod' });
export const query = (sql) => dbConnection.run(sql);

// app.js
import { query } from './database.js';
import { KeyFacts, ProTip } from '../../components/LessonUI';
// Importer AUTOMATICALLY waits for 'database.js' to resolve its 
// Top-Level Awaits before running its own code.
const results = await query('SELECT * FROM users');`;

const conditionalImportCode = `// Advanced Pattern: Dynamic Dependency Loading
const isProduction = process.env.NODE_ENV === 'production';

// Dynamically import only what's needed for the environment
const logger = isProduction 
    ? await import('./prod-logger.js') 
    : await import('./dev-logger.js');

export const log = logger.default;`;

const JSTopLevelAwait = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Asynchronous Architecture</div>
                <h1>Top-Level Await: Module Orchestration</h1>
                <p className="hero-lead">
                    Transform how your application initializes. Learn to use `await`
                    outside of `async` functions to manage module-level dependencies,
                    dynamic imports, and resource allocation with precision.
                </p>
            </div>

            <div className="content-section">
                <h2>The Async Evolution</h2>
                <p>
                    Historically, the `await` keyword was strictly bound to the scope
                    of an `async` function. To initialize a module asynchronously,
                    engineers were forced to use **Immediate Invoked Function Expressions
                    (IIFEs)** or complex promise chains. **Top-Level Await** elevates
                    the module itself to an asynchronous context, allowing for cleaner,
                    declarative initialization logic.
                </p>
                <CodeBlock code={topLevelAwaitCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Conditional Dependency Resolution</h2>
                <p>
                    One of the most powerful use cases for Top-Level Await is **Conditional
                    Loading**. Instead of bloating your bundle with every possible
                    dependency, you can use dynamic `import()` combined with `await`
                    to load only the necessary logic based on the environment, feature
                    flags, or browser capabilities.
                </p>
                <CodeBlock code={conditionalImportCode} language="javascript" />
            </div>

            <ProTip><p>
                    Top-Level Await is a double-edged sword. While it simplifies
                    initialization, it **blocks the evaluation** of the current module
                    and any other module that imports it. This ensures that when you
                    `import` a variable, it is fully resolved and ready to use.
                    However, a slow network request in a Top-Level Await can delay
                    your entire app's startup. Always use high-performance CDNs or
                    timeouts when using this pattern.
                </p></ProTip>

            <div className="content-section">
                <h2>Comparison: IIFE vs. Top-Level Await</h2>
                <p>
                    Previously, if a module needed to load data before exporting, you
                    might have exported a `Promise`. Importers then had to `await`
                    that promise every time they used the module. Top-Level Await
                    shifts that burden to the **Module Loader**, making the
                    imported value immediately available as a final result.
                </p>
            </div>

            <KeyFacts><ul>
                    <li>**Modularity:** Use for essential initialization like DB connections or API keys.</li>
                    <li>**Performance:** Keep Top-Level Awaits as shallow/fast as possible to avoid boot-up lag.</li>
                    <li>**Safety:** Always wrap Top-Level Awaits in `try/catch` to handle network or initialization failures.</li>
                    <li>**Alternatives:** For non-critical data, consider exporting a `Promise` instead of blocking the module.</li>
                    <li>**Capability:** Remember this feature is only available in **ES Modules**, not standard scripts.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSTopLevelAwait;


